import config from 'common/config'
import React, { useCallback, useMemo } from 'react'
import { take } from 'lodash'
import { useCurrentUserId } from 'universal/hooks'

import { cn, logger } from 'common/utils'
const log = logger.extend('ShareButtons')

// TODO: OPTIMIZE - use webpack's dynamic imports to only load the parts we need
// Full docs for each: https://github.com/nygardk/react-share
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TwitterShareButton,
  PinterestShareButton,
  TumblrShareButton,
  WhatsappShareButton,
  // HatenaShareButton, InstapaperShareButton, LineShareButton, LivejournalShareButton, MailruShareButton, OKShareButton, PocketShareButton, TelegramShareButton, ViberShareButton, VKShareButton, WorkplaceShareButton,
  EmailIcon,
  FacebookIcon,
  LinkedinIcon,
  RedditIcon,
  TwitterIcon,
  PinterestIcon,
  TumblrIcon,
  WhatsappIcon,
  // FacebookMessengerIcon, HatenaIcon, InstapaperIcon, LineIcon, LivejournalIcon, MailruIcon, OKIcon, PocketIcon, TelegramIcon, ViberIcon, VKIcon, WeiboIcon, WorkplaceIcon,

  // FacebookShareCount,
  // PinterestShareCount,
  // TumblrShareCount,
  // RedditShareCount,
  // HatenaShareCount, OKShareCount, VKShareCount,
} from 'react-share'

const Buttons = {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TwitterShareButton,

  PinterestShareButton,
  TumblrShareButton,
  WhatsappShareButton,
  // HatenaShareButton, InstapaperShareButton, LineShareButton, LivejournalShareButton, MailruShareButton, OKShareButton, PocketShareButton, TelegramShareButton, ViberShareButton, VKShareButton, WorkplaceShareButton
}

const Icons = {
  EmailIcon,
  FacebookIcon,
  LinkedinIcon,
  RedditIcon,
  TwitterIcon,

  PinterestIcon,
  TumblrIcon,
  WhatsappIcon,
  // FacebookMessengerIcon, HatenaIcon, InstapaperIcon, LineIcon, LivejournalIcon, MailruIcon, OKIcon, PocketIcon, TelegramIcon, ViberIcon, VKIcon, WeiboIcon, WorkplaceIcon
}

// TODO: unable FB, Reddit to show share counts once we have a page to share
const Counts = {
  // FacebookShareCount,
  // PinterestShareCount,
  // TumblrShareCount,
  //  RedditShareCount,
  // HatenaShareCount, OKShareCount, VKShareCount,
}

const suffixLength = 'ShareButton'.length
const SUPPORTED_NETWORKS = Object.keys(Buttons).map((name) => name.substring(0, name.length - suffixLength))

// Note: window opening blocked until promise is resolved
const makeShareWindowOpenHandler = (network) => async () => {
  log('onShareWindowOpen', network)
}

// Note: seems to fire when the opened window navigates away, e.g. to a login screen for the underlying network.
// There does NOT seem to be any clear trigger for "this was successfully shared" :/
const makeShareWindowCloseHandler = (network) => () => {
  log('onShareWindowClose', network)
  // TODO: track user clicks and move their most used networks higher up the priority chain (cautious w/ SSG)
}

const ShareCount = React.memo(({ network, url }) => {
  const CountTag = Counts[`${network}ShareCount`] // Pretty sure this shouldn't be necessary... and yet
  if (!CountTag) return null

  return (
    <CountTag url={url}>
      {(shareCount) => <span className="text-white bg-blue-300 rounded-full px-2 py-1 text-sm">{shareCount}</span>}
    </CountTag>
  )
})
ShareCount.displayName = 'ShareCount'

const Icon = React.memo(({ network, skipLabel, ...props }) => {
  const IconTag = Icons[`${network}Icon`] // Pretty sure this shouldn't be necessary... and yet

  return (
    <div className="relative flex flex-col m-1 items-center group">
      <IconTag size={46} round {...props} />
      {skipLabel ? null : (
        <div
          className={cn(
            'small',
            // Hack to make btns fancy on user page without messing w/ extension
            props.size &&
              props.size < 40 &&
              'text-gray-500 transition-opacity duration-300 opacity-0 group-hover:opacity-100 absolute bottom-6 mb-2',
          )}
        >
          {network.toLowerCase()}
        </div>
      )}
      <ShareCount network={network} url={props.url} />
    </div>
  )
})
Icon.displayName = 'Icon'

const FullShareButton = React.memo(({ network, size, iconProps, ...props }) => {
  const ButtonTag = Buttons[`${network}ShareButton`]

  return (
    <ButtonTag
      {...props}
      className="filter grayscale hover:filter-none"

      // TODO: works, but what should we track? via network, via sharing user, via review leaving user... what's useful for us?
      // onClick={() => alert(`clicked ${network}`)}
    >
      <Icon network={network} size={size} {...iconProps} />
    </ButtonTag>
  )
})
FullShareButton.displayName = 'FullShareButton'

const activeNetworksForUserId = (uid, num) => {
  // TODO: POLISH - add per-user customization of which networks to show

  return take(SUPPORTED_NETWORKS, num)
}

const ShareButtons = ({
  num = 5,
  btnClassName,
  size,

  // Optional props
  iconProps,

  // From useShareableReviewComponents (any future shareable items should use same interface)
  url,
  title,
  summary,
  emailBody,
  emailSubject,
  fbQuote,
  displayHashtag,
  hashtags,
}) => {
  const uid = useCurrentUserId()
  const activeNetworks = useMemo(() => activeNetworksForUserId(uid, num), [uid, num])

  const ConditionalShareButton = useCallback(
    ({ network, ...props }) =>
      activeNetworks.includes(network) ? (
        <FullShareButton
          network={network}
          url={url}
          onShareWindowClose={makeShareWindowCloseHandler(network)}
          beforeOnClick={makeShareWindowOpenHandler(network)}
          {...props}
          className={btnClassName}
          size={size}
          iconProps={iconProps}
        />
      ) : null,
    [url, btnClassName, activeNetworks],
  )

  const imageUrl = null // TODO

  return (
    <div className="flex flex-wrap justify-center mt-3">
      <ConditionalShareButton network="Email" subject={emailSubject} body={emailBody} />
      <ConditionalShareButton network="Facebook" quote={fbQuote} hashtag={displayHashtag} />
      <ConditionalShareButton network="Linkedin" title={title} summary={summary} source={config.siteRootUrl} />
      <ConditionalShareButton network="Reddit" title={title} />

      {/* TODO: see if hashtags need # prefixing */}
      {/* TODO: see 'related' prop if we have twitter accounts for currentUser... or is this for our own twitter account? */}
      {/* TODO: check out via display - would be prefer the app name?  */}
      <ConditionalShareButton network="Twitter" title={title} hashtags={hashtags} via={config.siteRootUrl} />

      {config.fbAppId && <ConditionalShareButton network="FacebookMessenger" appId={config.fbAppId} />}
      <ConditionalShareButton network="Hatena" title={title} />
      <ConditionalShareButton network="Instapaper" title={title} description={summary} />
      <ConditionalShareButton network="Line" title={title} />
      <ConditionalShareButton network="Livejournal" title={title} description={summary} />
      <ConditionalShareButton network="Mailru" title={title} description={summary} imageUrl={imageUrl} />
      <ConditionalShareButton network="OKShare" title={title} description={summary} image={imageUrl} />

      {/* TODO: autogenerate a few text-on-static-background images for sharing here/instragram/etc? */}
      {imageUrl && <ConditionalShareButton network="Pinterest" media={imageUrl} description={title} />}
      <ConditionalShareButton network="Pocket" title={title} />
      <ConditionalShareButton network="Telegram" title={title} />

      {/* TODO: see if hashtags need # prefixing */}
      <ConditionalShareButton network="Tumblr" title={title} tags={hashtags} caption={summary} />

      <ConditionalShareButton network="Viber" title={title} separator=": " />
      <ConditionalShareButton network="VK" title={title} image={imageUrl} noVkLinks />
      <ConditionalShareButton network="Weibo" title={title} image={imageUrl} />
      <ConditionalShareButton network="Whatsapp" title={title} separator=": " />
      <ConditionalShareButton network="Workplace" quote={fbQuote} hashtag={displayHashtag} />
    </div>
  )
}

export default ShareButtons
